import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { apiLabs } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalStyles, PageWrap, Header, FilterBar, Field, Input, Select, Actions, Btn,
  ResultsBar, List, ListHead, LogItem, LogHeader, StatusBadge, LogBody, Json,
  ResendBar, ConfirmOverlay, ConfirmBox,
  Pagination, PageBtn, Empty, Loading,
} from './style'

const PER_PAGE_OPTIONS = [50, 100, 200, 500]
const DEFAULT_PER_PAGE = 100

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// Datetime literal (sem conversão de timezone): "2026-06-23T13:35:22Z" → 23/06/2026 13:35:22
function fmtDateTime(s) {
  if (!s) return '—'
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/)
  if (!m) return String(s)
  const [, y, mo, d, h, mi, se] = m
  return `${d}/${mo}/${y} ${h}:${mi}:${se}`
}

// Formata o response (objeto ou string JSON) de forma legível.
function prettyJson(val) {
  if (val == null || val === '') return '—'
  if (typeof val === 'string') {
    try { return JSON.stringify(JSON.parse(val), null, 2) } catch (e) { return val }
  }
  try { return JSON.stringify(val, null, 2) } catch (e) { return String(val) }
}

// Acessores defensivos (não sei o casing exato vindo do backend)
const getOrderCode = (it) => it.order_code ?? it.Order ?? it.order ?? '—'
const getIdOrder = (it) => it.id_order ?? it.IDOrder ?? it.idOrder ?? '—'
const getStatus = (it) => String(it.status ?? '').toLowerCase()
const getMessage = (it) => it.message ?? it.comments ?? it.Comments ?? ''
const getCreatedAt = (it) => it.created_at ?? it.createdAt ?? it.created ?? it.data ?? null
const getResponse = (it) => it.response ?? it.Response ?? null

/**
 * Data que o evento gravou no pedido — é ela que o reenvio regrava.
 *
 * Só evento de SUCESSO e que realmente tenha gravado data. Existem sucessos
 * que pularam a gravação (data já vinha calculada da integração); o backend
 * recusa esses com 422, então nem oferecemos o botão.
 */
function getResendableDate(it) {
  if (getStatus(it) !== 'sucesso') return null
  let response = getResponse(it)
  if (typeof response === 'string') {
    try { response = JSON.parse(response) } catch (e) { return null }
  }
  return response?.ShippingEstimateHandlingLimitDate || null
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function IntegracaoEmbarque(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // Campos do formulário de filtro
  const [fDe, setFDe] = useState('')
  const [fAte, setFAte] = useState('')
  const [fIdOrder, setFIdOrder] = useState('')
  const [fOrderCode, setFOrderCode] = useState('')
  const [fStatus, setFStatus] = useState('')

  // Filtros aplicados (só mudam ao clicar "Buscar")
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  const [data, setData] = useState({ items: [], total: 0, page: 1, perPage: DEFAULT_PER_PAGE, totalPages: 1 })
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())

  // Reenvio da data: confirmação pendente e evento em andamento
  const [resendConfirm, setResendConfirm] = useState(null)
  const [resendingId, setResendingId] = useState(null)

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, perPage }
      if (filters.de) params.de = filters.de
      if (filters.ate) params.ate = filters.ate
      if (filters.idOrder) params.id_order = filters.idOrder
      if (filters.orderCode) params.order_code = filters.orderCode
      if (filters.status) params.status = filters.status

      const res = await apiLabs.get('embarque/logs', { params })
      const d = res.data || {}
      setData({
        items: Array.isArray(d.items) ? d.items : [],
        total: d.total ?? 0,
        page: d.page ?? page,
        perPage: d.perPage ?? perPage,
        totalPages: d.totalPages ?? 1,
      })
      setExpanded(new Set())
    } catch (err) {
      console.error('GET embarque/logs failed:', err)
      toast.error('Erro ao carregar logs: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
      setData({ items: [], total: 0, page: 1, perPage, totalPages: 1 })
    } finally {
      setLoading(false)
    }
  }, [filters, page, perPage])

  useEffect(() => {
    if (auth === 'authorized') fetchLogs()
  }, [auth, fetchLogs])

  /**
   * Regrava no pedido a data que ESTE evento gravou.
   *
   * Não passa pelo cálculo nem pela trava WRITE_ONCE — é escrita direta no
   * ERP, por isso a confirmação antes. O próprio reenvio vira um evento novo
   * no log, então a lista é recarregada no fim.
   */
  async function confirmResend() {
    if (!resendConfirm) return
    const { logId, orderCode, date } = resendConfirm

    setResendConfirm(null)
    setResendingId(logId)
    try {
      const res = await apiLabs.post(`embarque/logs/${logId}/reenviar`)
      const { anterior, ShippingEstimateHandlingLimitDate: nova } = res.data || {}
      toast.success(
        `${orderCode}: ${anterior ? fmtDateTime(anterior) : '—'} → ${fmtDateTime(nova || date)}`
      )
      await fetchLogs()
    } catch (err) {
      console.error('POST embarque/logs/:id/reenviar failed:', err)
      const httpStatus = err?.response?.status
      const backendMessage =
        err?.response?.data?.error || err?.response?.data?.message || err.message

      // O backend já devolve mensagem específica nos 422 (evento sem data,
      // sem id_order, pedido fora do ERP). Nos demais, o motivo é de infra.
      if (httpStatus === 429) {
        toast.warn('O ERP recusou por excesso de requisições. Tente de novo em instantes.')
      } else if (httpStatus === 404) {
        toast.error('Evento não encontrado — a lista pode estar desatualizada.')
      } else {
        toast.error(`Não foi possível reenviar: ${backendMessage}`)
      }
    } finally {
      setResendingId(null)
    }
  }

  // ── HANDLERS ────────────────────────────────────────────────────────────────
  function applyFilters() {
    setFilters({
      de: fDe.trim(),
      ate: fAte.trim(),
      idOrder: fIdOrder.trim(),
      orderCode: fOrderCode.trim(),
      status: fStatus,
    })
    setPage(1)
  }
  function clearFilters() {
    setFDe(''); setFAte(''); setFIdOrder(''); setFOrderCode(''); setFStatus('')
    setFilters({})
    setPage(1)
  }
  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function onFilterKeyDown(e) {
    if (e.key === 'Enter') applyFilters()
  }

  const totalPages = data.totalPages || 1
  const currentPage = data.page || page

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Integração Embarque">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalStyles />
          <PageWrap>
            <Header>
              <h1>Integração · Embarque</h1>
              <p>Logs da integração que gera e atualiza o pedido com a data de embarque.</p>
            </Header>

            <FilterBar>
              <Field>
                De
                <Input type="date" value={fDe} onChange={(e) => setFDe(e.target.value)} />
              </Field>
              <Field>
                Até
                <Input type="date" value={fAte} onChange={(e) => setFAte(e.target.value)} />
              </Field>
              <Field>
                ID do pedido
                <Input
                  placeholder="18306877"
                  value={fIdOrder}
                  onChange={(e) => setFIdOrder(e.target.value)}
                  onKeyDown={onFilterKeyDown}
                />
              </Field>
              <Field>
                Código do pedido
                <Input
                  placeholder="VX18306877 (ou parte)"
                  value={fOrderCode}
                  onChange={(e) => setFOrderCode(e.target.value)}
                  onKeyDown={onFilterKeyDown}
                />
              </Field>
              <Field>
                Status
                <Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="sucesso">Sucesso</option>
                  <option value="erro">Erro</option>
                </Select>
              </Field>
              <Actions>
                <Btn onClick={clearFilters} disabled={loading}>Limpar</Btn>
                <Btn $primary onClick={applyFilters} disabled={loading}>Buscar</Btn>
              </Actions>
            </FilterBar>

            <ResultsBar>
              <span><strong>{data.total}</strong> log{data.total !== 1 ? 's' : ''} encontrado{data.total !== 1 ? 's' : ''}</span>
              <div className="right">
                Por página
                <Select
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1) }}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Select>
              </div>
            </ResultsBar>

            <List>
              <ListHead>
                <div />
                <div>Data</div>
                <div>Pedido</div>
                <div>ID Pedido</div>
                <div>Status</div>
                <div>Mensagem</div>
              </ListHead>

              {loading && <Loading>Carregando logs…</Loading>}

              {!loading && data.items.length === 0 && (
                <Empty>Nenhum log encontrado para os filtros informados.</Empty>
              )}

              {!loading && data.items.map((it, i) => {
                const id = it.id ?? `${getOrderCode(it)}-${i}`
                const open = expanded.has(id)
                const status = getStatus(it)
                // Sem id do evento não há o que reenviar — a rota é por id
                const resendDate = it.id != null ? getResendableDate(it) : null
                return (
                  <LogItem key={id}>
                    <LogHeader $open={open} onClick={() => toggle(id)}>
                      <span className="chevron">{open ? '−' : '+'}</span>
                      <span className="dt">{fmtDateTime(getCreatedAt(it))}</span>
                      <span className="order">{getOrderCode(it)}</span>
                      <span className="idorder">{getIdOrder(it)}</span>
                      <StatusBadge $status={status}>{status || '—'}</StatusBadge>
                      <span className="msg" title={getMessage(it)}>{getMessage(it) || '—'}</span>
                    </LogHeader>
                    {open && (
                      <LogBody>
                        {getMessage(it) ? (
                          <div className="meta"><b>Mensagem:</b> {getMessage(it)}</div>
                        ) : null}

                        {resendDate && (
                          <ResendBar>
                            <div className="info">
                              Regrava no pedido a data deste evento:
                              <strong>{fmtDateTime(resendDate)}</strong>
                            </div>
                            <div className="spacer" />
                            <Btn
                              type="button"
                              disabled={resendingId != null}
                              onClick={() => setResendConfirm({
                                logId: it.id,
                                orderCode: getOrderCode(it),
                                date: resendDate,
                              })}
                            >
                              {resendingId === it.id ? 'Reenviando…' : '↻ Reenviar data'}
                            </Btn>
                          </ResendBar>
                        )}

                        <div className="jsonlabel">Response</div>
                        <Json>{prettyJson(getResponse(it))}</Json>
                      </LogBody>
                    )}
                  </LogItem>
                )
              })}
            </List>

            {!loading && data.items.length > 0 && (
              <Pagination>
                <PageBtn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                  ← Anterior
                </PageBtn>
                <span>Página <strong>{currentPage}</strong> de {totalPages}</span>
                <PageBtn onClick={() => setPage((p) => p + 1)} disabled={currentPage >= totalPages}>
                  Próxima →
                </PageBtn>
              </Pagination>
            )}

            {resendConfirm && (
              <ConfirmOverlay onClick={() => setResendConfirm(null)} role="presentation">
                <ConfirmBox onClick={(e) => e.stopPropagation()}>
                  <h3>Reenviar data do evento</h3>
                  <p>
                    A data que este evento gravou vai ser regravada no pedido{' '}
                    <strong>{resendConfirm.orderCode}</strong>.
                  </p>
                  <div className="change">
                    <span className="to">{fmtDateTime(resendConfirm.date)}</span>
                    <span className="arrow">→ pedido {resendConfirm.orderCode}</span>
                  </div>
                  <div className="warn">
                    Escrita direta no ERP, sem passar pelo cálculo. Fica registrada
                    no log como um evento novo.
                  </div>
                  <div className="actions">
                    <Btn type="button" onClick={() => setResendConfirm(null)}>
                      Cancelar
                    </Btn>
                    <Btn $primary type="button" onClick={confirmResend}>
                      Reenviar data
                    </Btn>
                  </div>
                </ConfirmBox>
              </ConfirmOverlay>
            )}
          </PageWrap>
        </>
      )}
    </Dashboard>
  )
}
